<!-- eslint-disable vue/no-mutating-props -->
<template>
  <transition name="modal-fade">
    <div class="modal-backdrop">
      <v-dialog
        class="modal"
        max-width="900"
        v-model="dialog"
        aria-labelledby="modalTitle"
        aria-describedby="modalDescription"
        content-class="sc-modal sc-modal--lg"
        scrollable
      >
        <template v-slot:activator="{ on, attrs }">
          <v-tooltip left color="surface_neutral_selected">
        <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
          <div class="card-action-box card-action-box--edit" v-bind="{ ...attrs, ...tooltipAttrs }" v-on="{ ...on, ...tooltipOn }" role="button" aria-label="Editar">
            <v-icon>mdi-pencil</v-icon>
          </div>
        </template>
        <span>Editar</span>
      </v-tooltip>
        </template>

        <v-card v-if="dialog" flat class="sc-modal__card" :key="colaboracaoData.id_colaboracao">
          <!-- Close button -->
          <v-btn icon class="sc-modal__close" @click="close(true)">
            <v-icon>mdi-close</v-icon>
          </v-btn>

          <!-- Hero Header -->
          <div class="hero-header">
            <!-- Avatar -->
            <div class="hero-image-container">
              <template v-if="!modalData.flg_sem_avatar && modalData.url_avatar_colaborador">
                <v-img
                  v-if="!heroAvatarError"
                  class="hero-image"
                  :src="modalData.url_avatar_colaborador"
                  alt="Avatar"
                  @error="heroAvatarError = true"
                >
                  <div class="image-overlay">
                    <v-btn
                      icon small class="image-action-btn"
                      @click="modalData.flg_sem_avatar = true"
                    >
                      <v-icon small>mdi-eye-off</v-icon>
                    </v-btn>
                  </div>
                </v-img>
                <div v-else class="hero-image-placeholder">
                  <img src="@/assets/User.png" alt="Avatar" style="width: 80px; height: 80px; object-fit: contain; opacity: 0.6;" />
                </div>
              </template>
              <template v-else>
                <div
                  class="hero-image-placeholder"
                  @click="modalData.flg_sem_avatar = false"
                >
                  <v-icon size="40" color="text_secondary">mdi-account-outline</v-icon>
                  <span class="placeholder-text">{{ modalData.flg_sem_avatar ? 'Mostrar avatar' : 'Sem avatar' }}</span>
                </div>
              </template>
            </div>

            <!-- Hero content -->
            <div class="hero-content">
              <div class="inline-edit-container">
                <transition name="edit-transition" mode="out-in" @after-enter="onNameTransitionComplete">
                  <div
                    v-if="!isEditingName"
                    key="display"
                    class="name-display"
                    @click="startEditingName"
                  >
                    <h2 class="hero-name">{{ modalData.dsc_nome_colaborador || 'Nome do colaborador' }}</h2>
                    <v-icon small class="name-edit-icon">mdi-pencil</v-icon>
                  </div>
                  <div v-else key="edit" class="name-edit">
                    <div class="name-input-wrapper">
                      <input
                        ref="nameInput"
                        v-model="modalData.dsc_nome_colaborador"
                        class="name-input"
                        placeholder="Nome do colaborador"
                        @keydown.enter="finishEditingName"
                        @keydown.esc="cancelEditingName"
                        @blur="onNameBlur"
                      />
                      <div class="name-input-border"></div>
                    </div>
                  </div>
                </transition>
              </div>
              <!-- Read-only info -->
              <div class="hero-meta-row">
                <span class="hero-meta-chip">
                  <v-icon x-small>mdi-share-variant</v-icon>
                  {{ capitalize(colaboracaoData.dsc_fonte_colaboracao) }}
                </span>
                <span class="hero-meta-chip">
                  <v-icon x-small>mdi-clock-outline</v-icon>
                  {{ formatDate(colaboracaoData.dat_colaboracao) }} · {{ formatHour(colaboracaoData.dat_colaboracao) }}
                </span>
                <span v-if="colaboracaoData.dsc_uf_colaborador" class="hero-meta-chip">
                  <v-icon x-small>mdi-map-marker-outline</v-icon>
                  {{ colaboracaoData.dsc_uf_colaborador }}
                  <template v-if="colaboracaoData.sigla_estado_colaborador">({{ colaboracaoData.sigla_estado_colaborador }})</template>
                </span>
                <span v-if="colaboracaoData.dsc_colaborador_phone" class="hero-meta-chip">
                  <v-icon x-small>mdi-phone-outline</v-icon>
                  {{ formatCellPhone(colaboracaoData.dsc_colaborador_phone) }}
                </span>
                <span v-if="colaboracaoData.dsc_colaborador_email" class="hero-meta-chip">
                  <v-icon x-small>mdi-email-outline</v-icon>
                  {{ colaboracaoData.dsc_colaborador_email }}
                </span>
                <span v-if="colaboracaoData.dsc_complemento" class="hero-meta-chip">
                  <v-icon x-small>mdi-text-short</v-icon>
                  {{ colaboracaoData.dsc_complemento }}
                </span>
                <span v-if="colaboracaoData.dsc_colaborador_user" class="hero-meta-chip">
                  <v-icon x-small>mdi-at</v-icon>
                  {{ colaboracaoData.dsc_colaborador_user }}
                </span>
              </div>

              <!-- Toggles -->
              <div class="toggle-group">
                <div class="toggle-item">
                  <v-switch
                    v-model="avatarVisible"
                    color="primary"
                    hide-details
                    dense
                  />
                  <div class="toggle-label">
                    <span class="toggle-title">Exibir avatar</span>
                    <span class="toggle-description">Mostrar foto do colaborador</span>
                  </div>
                </div>

                <div v-if="modalData.dsc_texto_colaboracao" class="toggle-item">
                  <v-switch
                    v-model="modalData.flg_exibir_texto"
                    color="primary"
                    hide-details
                    dense
                  />
                  <div class="toggle-label">
                    <span class="toggle-title">Exibir texto</span>
                    <span class="toggle-description">Mostrar texto na tarja</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Text Section -->
          <div class="edit-section">
            <div class="edit-section__header">
              <v-icon small class="mr-2">mdi-text</v-icon>
              <span>Texto da colaboração</span>
            </div>

            <v-textarea
              solo flat dense hide-details no-resize
              background-color="surface_tertiary"
              rows="4"
              :disabled="!Boolean(modalData.flg_exibir_texto)"
              v-model="modalData.dsc_texto_colaboracao"
              class="edit-textarea"
              placeholder="Texto da colaboração..."
            />
            <div class="d-flex justify-space-between align-center mt-1 px-1">
              <span
                v-if="modalData.dsc_texto_colaboracao.trim().length > 280"
                class="edit-char-warning"
              >
                Máximo 280 caracteres para tarja
              </span>
              <span v-else></span>
              <span class="edit-char-count">
                {{ modalData.dsc_texto_colaboracao.trim().length }}
              </span>
            </div>
          </div>

          <!-- Media Section -->
          <div v-if="modalData.midias.length > 0" class="media-section">
            <div class="edit-section__header">
              <v-icon small class="mr-2">mdi-image-multiple</v-icon>
              <span>Mídias da colaboração</span>
            </div>

            <div class="media-scroll-area">
              <div class="media-options">
                <div
                  v-for="media in modalData.midias"
                  :key="media.id_imagem"
                  class="media-option"
                  :class="[
                    { 'media-option--selected': media.flg_selecionada === 'S' },
                    `media-option--${media.dsc_tipo_midia_colaboracao}`
                  ]"
                >
                  <div class="media-option__thumb">
                    <template v-if="media.dsc_tipo_midia_colaboracao === 'audio'">
                      <div class="media-option__audio">
                        <v-icon size="32" color="text_secondary">mdi-waveform</v-icon>
                      </div>
                    </template>
                    <template v-else>
                      <img
                        :src="media.dsc_tipo_midia_colaboracao === 'image' ? media.url_midia_colaboracao : media.url_midia_thumbnail"
                        alt="Mídia"
                        @click.prevent="showModalImagem(media.url_midia_colaboracao, media.dsc_tipo_midia_colaboracao)"
                      />
                      <div v-if="media.dsc_tipo_midia_colaboracao === 'video'" class="media-option__play">
                        <v-icon size="20" color="white">mdi-play-circle-outline</v-icon>
                      </div>
                      <ModalEditarMidiaColaboracao
                        :media="media"
                        :num_coluna_telao="num_coluna_telao"
                      />
                    </template>
                  </div>
                  <div class="media-option__info">
                    <span class="media-option__type">
                      <v-icon x-small class="mr-1">{{ mediaIcon(media.dsc_tipo_midia_colaboracao) }}</v-icon>
                      {{ mediaLabel(media.dsc_tipo_midia_colaboracao) }}
                    </span>
                    <CardAudioPlayer
                      v-if="media.dsc_tipo_midia_colaboracao === 'audio' && media.url_midia_colaboracao"
                      :key="media.url_midia_colaboracao"
                      :src="media.url_midia_colaboracao"
                      class="media-option__player"
                    />
                    <div class="media-option__actions">
                      <v-switch
                        :input-value="media.flg_selecionada === 'S'"
                        @change="handleSelectMedia(media)"
                        color="primary"
                        hide-details
                        dense
                        class="mt-0 pt-0"
                      />
                      <span class="media-option__select-label">
                        {{ media.flg_selecionada === 'S' ? 'Selecionada' : 'Selecionar' }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Media Preview -->
          <ModalImagem
            :dialog="isModalImagemVisible"
            @close="closeModalImagem"
            :midia_url="midia_url"
            :media_type="media_type"
          />

          <!-- Footer -->
          <div class="sc-modal__footer">
            <v-btn text class="modal-btn-cancel" @click="close(true)">Cancelar</v-btn>
            <v-btn color="primary" class="modal-btn-confirm" @click.prevent="alterarColaboracao">Salvar</v-btn>
          </div>
        </v-card>
      </v-dialog>
      <ModalConfirmAction
        :visible="showConfirmDialog"
        title="Atualizar colaboração"
        message="Tem certeza que quer atualizar os dados da colaboração?"
        confirm-text="Confirmar"
        confirm-color="primary"
        @confirm="confirmAlterarColaboracao"
        @cancel="showConfirmDialog = false"
      />
    </div>
  </transition>
</template>

<script>
import { apiClient } from "@/services/api";
import { capitalizeString } from "@/utils/capitalizeString";
import { formatDate, formatHour, formatCellPhone } from "@/utils/dateUtils";
import ModalEditarMidiaColaboracao from "./ModalEditarMidiaColaboracao.vue";
import ModalImagem from "@/components/Modal/ModalImagem.vue";
import CardAudioPlayer from "@/components/Cards/CardAudioPlayer.vue";
import ModalConfirmAction from "../ModalConfirmAction.vue";
import { mapActions, mapGetters } from "vuex";

export default {
  components: { ModalEditarMidiaColaboracao, ModalImagem, ModalConfirmAction, CardAudioPlayer },
  data() {
    return {
      imagens: [],
      imagem_selecionada: [],
      isModalImagemVisible: false,
      midia_url: "",
      media_type: "",
      dialog: false,
      editImageDialog: false,
      heroAvatarError: false,
      id_selected_midia: null,
      modalData: {},
      showConfirmDialog: false,
      isEditingName: false,
      originalName: "",
    };
  },
  created() {
    const copyData = this.colaboracaoData;
    this.populateModalData(copyData);
  },
  props: {
    cod_perfil: Number,
    colaboracaoData: Object,
    tipoColaboracao: String,
    num_coluna_telao: Number,
    pageModerada: Number,
  },
  computed: {
    ...mapGetters({
      cod_pasta: "pastas/getterCodPasta",
    }),
    avatarVisible: {
      get() { return !this.modalData.flg_sem_avatar; },
      set(val) { this.modalData.flg_sem_avatar = !val; },
    },
  },
  watch: {
    dialog(val) {
      if (val) this.heroAvatarError = false;
    },
  },
  methods: {
    ...mapActions({
      getColaboracoesAprovadas: "colaboracoes_aprovadas/getColaboracoesAprovadas",
      getColaboracoesModeradas: "colaboracoes_moderadas/getColaboracoesModeradas",
      setCopyColaboracoesRecebidas: "colaboracoes_aprovadas/setCopyColaboracoesRecebidas",
    }),
    formatDate,
    formatHour,
    formatCellPhone,
    capitalize(string) {
      return capitalizeString(string);
    },
    mediaIcon(tipo) {
      if (tipo === "image") return "mdi-image";
      if (tipo === "audio") return "mdi-volume-high";
      return "mdi-video";
    },
    mediaLabel(tipo) {
      if (tipo === "image") return "Imagem";
      if (tipo === "audio") return "Áudio";
      return "Vídeo";
    },
    startEditingName() {
      this.originalName = this.modalData.dsc_nome_colaborador;
      this.isEditingName = true;
    },
    onNameTransitionComplete() {
      if (this.isEditingName && this.$refs.nameInput) {
        this.$refs.nameInput.focus();
      }
    },
    finishEditingName() {
      if (this.modalData.dsc_nome_colaborador.trim() === "") {
        this.modalData.dsc_nome_colaborador = this.originalName;
      }
      this.isEditingName = false;
    },
    cancelEditingName() {
      this.modalData.dsc_nome_colaborador = this.originalName;
      this.isEditingName = false;
    },
    onNameBlur() {
      setTimeout(() => {
        if (this.isEditingName) {
          this.finishEditingName();
        }
      }, 150);
    },
    populateModalData(data) {
      this.modalData = {
        cod_producao: Number(data.cod_producao),
        id_colaboracao: data.id_colaboracao,
        dsc_nome_colaborador: data.dsc_nome_colaborador,
        dsc_nome_colaborador_old: data.dsc_nome_colaborador,
        dsc_texto_colaboracao: data.dsc_texto_colaboracao,
        dsc_texto_colaboracao_old: data.dsc_texto_colaboracao,
        url_avatar_colaborador: data.url_avatar_colaborador,
        flg_sem_avatar: data.flg_sem_avatar === "N" ? false : true,
        dsc_tipo_midia_colaboracao: data.dsc_tipo_midia_colaboracao,
        flg_exibir_texto: data.flg_exibir_texto,
        flg_selecionada: data.flg_selecionada === "S" ? true : false,
        // Clone each media row so toggling flg_selecionada in the modal
        // doesn't mutate the card's underlying item — Cancelar must actually revert.
        midias: Array.isArray(data.midias) ? data.midias.map(m => ({ ...m })) : [],
      };
    },
    async showModalImagem(midia_url, media_type) {
      this.isModalImagemVisible = true;
      this.midia_url = midia_url;
      this.media_type = media_type;
    },
    async closeModalImagem() {
      this.isModalImagemVisible = false;
    },
    close(isCancelarEvent = false) {
      if (isCancelarEvent) {
        this.dialog = false;
        this.populateModalData(this.colaboracaoData);
      }
      this.$emit("close");
    },
    alterarColaboracao() {
      if (this.modalData.dsc_nome_colaborador.trim() === "") {
        this.$toast.error("Informe o nome do colaborador.");
        return;
      }

      if (
        this.modalData.dsc_texto_colaboracao.trim() === "" &&
        !this.colaboracaoData.url_midia_colaboracao &&
        !this.modalData.flg_selecionada
      ) {
        this.$toast.error("Informe a descrição da colaboração ou selecione uma mídia.");
        return;
      }

      if (
        this.modalData.dsc_texto_colaboracao.trim() === "" &&
        !this.modalData.flg_selecionada
      ) {
        this.$toast.error("Informe a descrição da colaboração ou selecione uma mídia.");
        return;
      }

      if (
        this.modalData.dsc_texto_colaboracao &&
        !this.modalData.flg_exibir_texto &&
        !this.modalData.flg_selecionada
      ) {
        this.$toast.error(
          "Nenhuma informação a ser visualizada, escolha alguma opção."
        );
        return;
      }

      this.showConfirmDialog = true;
    },
    async confirmAlterarColaboracao() {
      this.showConfirmDialog = false;
      try {
        if (
          this.modalData.dsc_nome_colaborador !== this.modalData.dsc_nome_colaborador_old
        ) {
          await apiClient.patch(
            `/atualizarNomeColaboracao/${this.modalData.id_colaboracao}`,
            {
              dsc_nome_colaborador: this.modalData.dsc_nome_colaborador,
              cod_producao: this.modalData.cod_producao,
            }
          );
          this.modalData.dsc_nome_colaborador_old = this.modalData.dsc_nome_colaborador;
        }

        if (
          this.modalData.dsc_texto_colaboracao !==
          this.modalData.dsc_texto_colaboracao_old
        ) {
          await apiClient.patch(
            `/atualizarTextoColaboracao/${this.modalData.id_colaboracao}`,
            {
              dsc_texto_colaboracao: this.modalData.dsc_texto_colaboracao,
              cod_producao: this.modalData.cod_producao,
            }
          );

          this.modalData.dsc_texto_colaboracao_old = this.modalData.dsc_texto_colaboracao;
        }

        await apiClient.patch(
          `/atualizarFlagAvatarColaboracao/${this.modalData.id_colaboracao}`,
          {
            cod_producao: this.modalData.cod_producao,
            flg_sem_avatar: this.modalData.flg_sem_avatar === true ? "S" : "N",
          }
        );

        await apiClient.patch(
          `/atualizarFlagExibirTextoColaboracao/${this.modalData.cod_producao}/${this.modalData.id_colaboracao}`,
          {
            flg_exibir_texto: this.modalData.flg_exibir_texto,
          }
        );

        for (const media of this.modalData.midias) {
          await apiClient.patch("/flagImagemColaboracao", {
            cod_producao: this.modalData.cod_producao,
            id_colaboracao: this.modalData.id_colaboracao,
            id_imagem: media.id_imagem,
            flg_selecionada: media.flg_selecionada,
          });
        }

        this.close();

        if (this.tipoColaboracao == "aprovadas") {
          const flg_favorita =
            JSON.parse(localStorage.getItem("@socialcam/flg_favorita")) || false;
          const flg_exibida =
            JSON.parse(localStorage.getItem("@socialcam/flg_exibida")) || false;
          const page = flg_exibida
            ? this.$store.state.colaboracoes_aprovadas.colaboracoes.colaboracoes_exibidas
                .currentPage
            : flg_favorita
            ? this.$store.state.colaboracoes_aprovadas.colaboracoes.colaboracoes_favoritas
                .currentPage
            : this.$store.state.colaboracoes_aprovadas.colaboracoes.colaboracoes_recebidas
                .currentPage;

          this.getColaboracoesAprovadas({
            page,
            size: 5,
            cod_producao: this.modalData.cod_producao,
            flg_favorita,
            flg_exibida,
            cod_pasta: this.cod_pasta,
          }).then(() => {
            this.setCopyColaboracoesRecebidas(
              this.$store.state.colaboracoes_aprovadas.colaboracoes.colaboracoes_recebidas
            );
            this.$toast.success("Colaboração alterada com sucesso!");
            this.dialog = false;
          });
        } else if (this.tipoColaboracao == "moderadas") {
          this.getColaboracoesModeradas({
            num_coluna_telao: this.num_coluna_telao,
            page: this.pageModerada,
            size: 5,
            cod_producao: this.modalData.cod_producao,
            filtro_texto_moderada: "",
          }).then(() => {
            this.$toast.success("Colaboração alterada com sucesso!");
            this.dialog = false;
          });
        }
      } catch (error) {
        console.error(error);
        return this.$toast.error("Erro");
      }
    },
    handleSelectMedia(media) {
      const isSelecting = media.flg_selecionada !== 'S';
      // Deselect all
      this.modalData.midias.forEach(m => { m.flg_selecionada = 'N'; });
      // Select the clicked one
      if (isSelecting) {
        media.flg_selecionada = 'S';
        this.modalData.flg_selecionada = true;
      } else {
        this.modalData.flg_selecionada = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// Modal shell (sc-modal__card / sc-modal__close / sc-modal__footer) comes from
// _modal.scss. This block carries the V2 hero pattern: hero header with
// avatar, inline name edit, meta chips, toggle group, plus the edit-section /
// media-option widgets specific to the colaboração editor.

/* Hero Header */
.hero-header {
  display: flex;
  gap: 24px;
  padding: 24px;
  background: linear-gradient(135deg, var(--v-surface_tertiary-base) 0%, var(--v-surface_secondary-base) 100%);
  border-bottom: 1px solid var(--sc-border-subtle);
}

.hero-image-container {
  flex-shrink: 0;
  width: 200px;
  height: 140px;
  border-radius: var(--sc-radius-lg);
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;

  .image-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, transparent 50%);
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
    padding: 8px;
    gap: 4px;
    opacity: 0;
    transition: opacity var(--sc-duration-base) var(--sc-easing-standard);
  }

  &:hover .image-overlay {
    opacity: 1;
  }
}

.image-action-btn {
  background: rgba(255, 255, 255, 0.15) !important;
  backdrop-filter: blur(4px);

  &:hover {
    background: rgba(255, 255, 255, 0.25) !important;
  }
}

.hero-image-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, var(--v-surface_neutral-base) 0%, var(--v-surface_tertiary-base) 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all var(--sc-duration-base) var(--sc-easing-standard);
  border: 2px dashed var(--sc-border-default);
  border-radius: var(--sc-radius-lg);

  &:hover {
    border-color: var(--v-primary-base);
    .v-icon { color: var(--v-primary-base) !important; }
  }

  .placeholder-text {
    font-size: 0.7rem;
    color: var(--v-text_secondary-base);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.hero-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  padding-right: 40px;
}

/* Inline name editor */
.inline-edit-container {
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  margin-bottom: 4px;
}

.name-display {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: var(--sc-radius-md);
  transition: all 0.25s var(--sc-easing-standard);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: var(--sc-radius-md);
    background: rgba(255, 255, 255, 0);
    transition: background 0.25s var(--sc-easing-standard);
  }

  &:hover {
    &::before { background: var(--sc-surface-tint-hover); }
    .name-edit-icon { opacity: 0.8; transform: scale(1); }
  }

  &:active { transform: scale(0.98); }
}

.hero-name {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--v-text_primary-base);
  margin: 0;
  line-height: 1.3;
  position: relative;
  z-index: 1;
}

.name-edit-icon {
  opacity: 0;
  transform: scale(0.8);
  transition: all 0.25s var(--sc-easing-standard);
  color: var(--v-text_secondary-base);
  position: relative;
  z-index: 1;
}

.name-edit {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.name-input-wrapper {
  position: relative;
  display: inline-flex;
}

.name-input {
  background: var(--sc-surface-tint);
  border: none;
  border-radius: var(--sc-radius-md);
  padding: 6px 12px;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--v-text_primary-base);
  outline: none;
  transition: all 0.2s var(--sc-easing-standard);
  font-family: inherit;
  position: relative;
  z-index: 1;
  width: 350px;
  max-width: 100%;
}

.name-input-border {
  position: absolute;
  inset: 0;
  border: 2px solid var(--v-primary-base);
  border-radius: var(--sc-radius-md);
  pointer-events: none;
  animation: name-border-pulse 0.4s var(--sc-easing-standard);
}

@keyframes name-border-pulse {
  0% { transform: scale(0.95); opacity: 0; }
  50% { transform: scale(1.02); }
  100% { transform: scale(1); opacity: 1; }
}

/* Edit transition */
.edit-transition-enter-active {
  animation: edit-in 0.25s var(--sc-easing-standard);
}
.edit-transition-leave-active {
  animation: edit-out 0.2s var(--sc-easing-standard);
}
@keyframes edit-in {
  0% { opacity: 0; transform: translateY(-8px) scale(0.96); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes edit-out {
  0% { opacity: 1; transform: translateY(0) scale(1); }
  100% { opacity: 0; transform: translateY(8px) scale(0.96); }
}

.hero-meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 14px;
}

.hero-meta-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  color: var(--v-text_secondary-base);
  background: var(--sc-surface-tint-hover);
  padding: 3px 8px;
  border-radius: var(--sc-radius-sm);
  border: 1px solid var(--sc-border-subtle);

  .v-icon {
    opacity: 0.6;
  }
}

/* Toggle Switches */
.toggle-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.toggle-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background: var(--sc-surface-tint);
  border-radius: var(--sc-radius-md);
  transition: all var(--sc-duration-base) var(--sc-easing-standard);

  &:hover {
    background: var(--sc-surface-tint-hover);
  }
}

.toggle-label {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.toggle-title {
  font-size: var(--sc-text-base);
  font-weight: 500;
  color: var(--v-text_primary-base);
}

.toggle-description {
  font-size: 0.7rem;
  color: var(--v-text_secondary-base);
}

/* Sections */
.edit-section {
  padding: 20px 24px 0;
}

.edit-section__header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--v-text_secondary-base);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.edit-textarea {
  border-radius: var(--sc-radius-md) !important;
  border: 1px solid var(--sc-border-emphasis) !important;
  overflow: hidden;
  font-size: var(--sc-text-base) !important;

  &.v-input--is-focused {
    border-color: var(--v-primary-base) !important;
  }
}

.edit-char-count {
  font-size: 0.7rem;
  color: var(--v-text_secondary-base);
}

.edit-char-warning {
  font-size: 0.7rem;
  color: var(--v-surface_error_default-base);
}

/* Media Grid */
/* Media Section */
.media-section {
  padding: 20px 24px 0;
  overflow: hidden;
}

.media-scroll-area {
  max-height: 300px;
  overflow-y: auto;
}

.media-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.media-option {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px;
  background: var(--v-surface_tertiary-base);
  border-radius: var(--sc-radius-lg);
  border: 2px solid var(--sc-border-subtle);
  transition: border-color var(--sc-duration-base) var(--sc-easing-standard);

  &--selected {
    border-color: var(--v-primary-base);
    background: rgba(var(--sc-color-primary-rgb),0.04);
  }

  &:hover {
    border-color: var(--sc-border-medium);
  }
}

.media-option__thumb {
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  border-radius: var(--sc-radius-md);
  overflow: hidden;
  position: relative;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.media-option__play {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
}

.media-option__audio {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--sc-surface-tint-hover);
  border: 1px solid var(--sc-border-subtle);
  border-radius: var(--sc-radius-md);
}

.media-option--audio .media-option__thumb {
  cursor: default;
}

.media-option__info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.media-option__type {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--v-text_secondary-base);
  display: flex;
  align-items: center;
}

.media-option__actions {
  display: flex;
  align-items: center;
  gap: 12px;

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
}

.media-option__select-label {
  font-size: 0.75rem;
  color: var(--v-text_secondary-base);
  cursor: pointer;
}

/* Deep switch styles */
::v-deep .v-input--switch {
  margin: 0;
  padding: 0;
}

::v-deep .v-input--switch__track {
  opacity: 0.3;
}

/* Animations */
.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}
</style>
